/**
 * Synthesised interface sounds (Web Audio, no audio files).
 *
 * Design rules:
 *  - feedback sounds are short (≤ 600 ms) and soft; an error is a gentle low
 *    tone, never a harsh buzzer — the goal is information, not punishment;
 *  - diagnostic and exam answers get a neutral tick so sound never reveals
 *    whether an assessment answer was right;
 *  - the master volume and on/off switch come from Settings.
 */

export type SoundName =
  | 'tap' | 'select' | 'correct' | 'wrong' | 'neutral' | 'xp' | 'levelup' | 'mastery'
  | 'achievement' | 'notify' | 'open' | 'combo' | 'hit' | 'hurt' | 'sparkle';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let enabled = true;
let volume = 0.6;

export function configureSound(on: boolean, vol: number) {
  enabled = on;
  volume = vol;
  if (master) master.gain.value = volume * 0.5;
}

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = volume * 0.5;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Browsers only allow audio after a user gesture; call from the first pointer/key event. */
export function unlockAudio() {
  if (enabled) audio();
}

function tone(freq: number, start: number, dur: number, type: OscillatorType, gain: number, glideTo?: number) {
  const a = ctx!;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + Math.min(0.02, dur / 4));
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(master!);
  o.start(start);
  o.stop(start + dur + 0.02);
}

function noise(start: number, dur: number, gain: number, from: number, to: number) {
  const a = ctx!;
  const buf = a.createBuffer(1, Math.ceil(a.sampleRate * dur), a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = 1.2;
  f.frequency.setValueAtTime(from, start);
  f.frequency.exponentialRampToValueAtTime(to, start + dur);
  const g = a.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + dur * 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(f).connect(g).connect(master!);
  src.start(start);
  src.stop(start + dur);
}

// Equal-tempered note frequencies used by the cues (C major / pentatonic, consonant intervals).
const N = { C4: 261.63, E4: 329.63, G4: 392, A4: 440, C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880, C6: 1046.5, E6: 1318.5, G6: 1568 };

let lastTap = 0;

export function play(name: SoundName, opts: { combo?: number } = {}) {
  if (!enabled || volume <= 0) return;
  const a = audio();
  if (!a || !master) return;
  const t = a.currentTime + 0.005;
  switch (name) {
    case 'tap': {
      if (t - lastTap < 0.05) return;
      lastTap = t;
      tone(1800, t, 0.035, 'sine', 0.05);
      break;
    }
    case 'select':
      tone(880, t, 0.07, 'triangle', 0.08);
      break;
    case 'neutral':
      tone(660, t, 0.08, 'sine', 0.09);
      tone(660, t + 0.09, 0.06, 'sine', 0.05);
      break;
    case 'correct':
      tone(N.C5, t, 0.16, 'triangle', 0.16);
      tone(N.E5, t + 0.08, 0.18, 'triangle', 0.15);
      tone(N.G5, t + 0.16, 0.32, 'sine', 0.14);
      break;
    case 'wrong':
      tone(N.E4, t, 0.16, 'sine', 0.13);
      tone(N.C4, t + 0.13, 0.26, 'sine', 0.12);
      break;
    case 'combo': {
      const step = Math.min(8, opts.combo ?? 1);
      const base = 523.25 * Math.pow(2, step / 12);
      tone(base, t, 0.12, 'triangle', 0.12);
      tone(base * 1.5, t + 0.06, 0.2, 'sine', 0.1);
      break;
    }
    case 'xp':
    case 'sparkle':
      [N.E6, N.G6, N.C6 * 2].forEach((f, i) => tone(f, t + i * 0.045, 0.12, 'sine', 0.05));
      break;
    case 'notify':
      tone(N.A5, t, 0.12, 'sine', 0.1);
      tone(N.E5, t + 0.1, 0.22, 'sine', 0.08);
      break;
    case 'open':
      noise(t, 0.22, 0.05, 600, 2400);
      break;
    case 'achievement':
      [N.C5, N.E5, N.G5, N.C6].forEach((f, i) => tone(f, t + i * 0.07, 0.4, 'triangle', 0.11));
      tone(N.E6, t + 0.3, 0.5, 'sine', 0.06);
      break;
    case 'mastery':
      [N.C5, N.E5, N.G5].forEach((f) => tone(f, t, 0.9, 'sine', 0.09));
      [N.C6, N.E6].forEach((f, i) => tone(f, t + 0.25 + i * 0.12, 0.6, 'triangle', 0.06));
      noise(t + 0.1, 0.6, 0.025, 2000, 6000);
      break;
    case 'levelup':
      [N.C5, N.E5, N.G5, N.C6, N.E6, N.G6].forEach((f, i) => tone(f, t + i * 0.08, 0.5, i < 3 ? 'triangle' : 'sine', 0.11));
      [N.C5, N.G5, N.C6].forEach((f) => tone(f, t + 0.55, 1.1, 'sine', 0.07));
      noise(t + 0.4, 0.9, 0.03, 1500, 7000);
      break;
    case 'hit':
      noise(t, 0.18, 0.12, 3000, 300);
      tone(220, t, 0.18, 'square', 0.05, 110);
      break;
    case 'hurt':
      tone(180, t, 0.3, 'sawtooth', 0.05, 90);
      break;
  }
}
